import { productFruits, type SurveyClosedEvent } from 'product-fruits'
import { useCallback, useEffect } from 'react'

import { LOCAL_STORAGE_CHAVE_PF_SURVEYS } from '@/config/constantes'
import { useAuth, useFeatureFlag } from '@/hooks'
import { obterUsuarioProductFruits } from '@/utils/product-fruits/obter-usuario-product-fruits'

interface DadosUsuarioProductFruits {
  estaAutenticado: boolean
  login?: string
  nome?: string
  email?: string
  productFruitsId: string
}

let productFruitsInicializado = false
let usuarioAtualProductFruits: string | null = null
let productFruitsDesabilitado = false
let tratamentoErrosRegistrado = false
let avisoEmitido = false

type SurveysRespondidasPorUsuario = Record<string, string[]>

function emitirAviso() {
  if (avisoEmitido) return

  avisoEmitido = true
  console.warn(
    'Product Fruits desabilitado por erro de configuracao/carregamento. Verifique a flag "product_fruits".'
  )
}

function desabilitarProductFruits() {
  productFruitsDesabilitado = true
  productFruitsInicializado = false
  emitirAviso()
}

function idProductFruitsValido(productFruitsId: string): boolean {
  const idSanitizado = productFruitsId.trim()
  if (!idSanitizado) return false

  // Espera token simples, evita URL/textos invalidos vindos da feature flag.
  return /^[A-Za-z0-9_-]{8,64}$/.test(idSanitizado)
}

function contemAssinaturaProductFruits(texto: string): boolean {
  const normalizado = texto.toLowerCase()

  return (
    normalizado.includes('productfruits') ||
    normalizado.includes('app.productfruits.com') ||
    normalizado.includes('runner.') ||
    normalizado.includes('loadforcurrentidentity')
  )
}

function ehErroProductFruits(reason: unknown): boolean {
  if (!reason) return false

  if (reason instanceof Error) {
    const texto = `${reason.message} ${reason.stack ?? ''}`.toLowerCase()
    return (
      (texto.includes('failed to fetch') || texto.includes('err_failed')) &&
      contemAssinaturaProductFruits(texto)
    )
  }

  if (typeof reason === 'string') {
    const texto = reason.toLowerCase()
    return (
      (texto.includes('failed to fetch') || texto.includes('err_failed')) &&
      contemAssinaturaProductFruits(texto)
    )
  }

  return false
}

function registrarTratamentoErrosProductFruits() {
  if (tratamentoErrosRegistrado) return

  window.addEventListener(
    'unhandledrejection',
    event => {
      if (!ehErroProductFruits(event.reason)) return

      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation?.()
      desabilitarProductFruits()
    },
    true
  )

  window.addEventListener(
    'error',
    event => {
      const mensagem = (event as ErrorEvent).message?.toLowerCase() ?? ''
      const arquivo = (event as ErrorEvent).filename?.toLowerCase() ?? ''
      const texto = `${mensagem} ${arquivo}`

      if (!contemAssinaturaProductFruits(texto)) return

      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation?.()
      desabilitarProductFruits()
    },
    true
  )

  tratamentoErrosRegistrado = true
}

function obterSurveysRespondidas(): SurveysRespondidasPorUsuario {
  try {
    const surveysRespondidas = localStorage.getItem(
      LOCAL_STORAGE_CHAVE_PF_SURVEYS
    )
    if (!surveysRespondidas) return {}

    const surveysRespondidasParseadas = JSON.parse(
      surveysRespondidas
    ) as SurveysRespondidasPorUsuario

    if (
      !surveysRespondidasParseadas ||
      typeof surveysRespondidasParseadas !== 'object'
    ) {
      return {}
    }

    return surveysRespondidasParseadas
  } catch {
    return {}
  }
}

function salvarSurveysRespondidas(
  surveysRespondidas: SurveysRespondidasPorUsuario
) {
  localStorage.setItem(
    LOCAL_STORAGE_CHAVE_PF_SURVEYS,
    JSON.stringify(surveysRespondidas)
  )
}

function registrarSurveyRespondida(usuario: string, surveyId: string) {
  const surveysRespondidas = obterSurveysRespondidas()
  const surveysUsuario = surveysRespondidas[usuario] ?? []

  if (surveysUsuario.includes(surveyId)) return

  surveysRespondidas[usuario] = [...surveysUsuario, surveyId]
  salvarSurveysRespondidas(surveysRespondidas)
}

function usuarioJaRespondeuSurvey(login: string, surveyId: string): boolean {
  const surveysRespondidas = obterSurveysRespondidas()
  return surveysRespondidas[login]?.includes(surveyId) ?? false
}

function inicializarProductFruits({
  productFruitsId,
  estaAutenticado,
  login,
  nome,
  email,
}: DadosUsuarioProductFruits) {
  registrarTratamentoErrosProductFruits()

  if (productFruitsDesabilitado) return

  if (!productFruitsId || !idProductFruitsValido(productFruitsId)) {
    desabilitarProductFruits()
    return
  }

  if (process.env.REACT_APP_NOME_CLIENTE !== 'Sanesul' && !estaAutenticado)
    return

  usuarioAtualProductFruits = obterUsuarioProductFruits(login)

  if (productFruitsInicializado) return
  productFruitsInicializado = true

  try {
    productFruits.init(productFruitsId.trim(), 'pt', {
      username: usuarioAtualProductFruits,
      firstname: nome?.split(' ')[0] ?? '',
      lastname: nome?.split(' ')[1] ?? '',
      email,
    })
  } catch {
    desabilitarProductFruits()
    return
  }

  window.addEventListener('productfruits_ready', () => {
    window.productFruits?.api?.surveys?.listen(
      'survey-closed',
      (event: unknown) => {
        const { surveyId, reason } = event as SurveyClosedEvent
        if (reason !== 'last_screen') return
        if (!usuarioAtualProductFruits) return

        registrarSurveyRespondida(usuarioAtualProductFruits, surveyId)
      }
    )
  })
}

function iniciarSurvey(surveyId: string) {
  if (productFruitsDesabilitado) return

  if (
    usuarioAtualProductFruits &&
    usuarioJaRespondeuSurvey(usuarioAtualProductFruits, surveyId)
  ) {
    return
  }

  window.productFruits?.api?.surveys?.startSurvey(surveyId)
}

interface UseProductFruits {
  iniciarSurvey: (surveyId: string) => void
}

export const useProductFruits = (): UseProductFruits => {
  const { estaAutenticado, login, nome, email } = useAuth()
  const SdkId = useFeatureFlag('product_fruits')?.SdkId

  useEffect(() => {
    if (!SdkId) return

    inicializarProductFruits({
      productFruitsId: SdkId,
      estaAutenticado,
      login,
      nome,
      email,
    })
  }, [SdkId, estaAutenticado, login, nome, email])

  const iniciar = useCallback((surveyId: string) => {
    iniciarSurvey(surveyId)
  }, [])

  return { iniciarSurvey: iniciar }
}
